import React, { useState, useRef } from 'react';
import makeSPI from '@atlassian/amkt-upm-spi-impl';
import {
	getAppInstallConfigForEnv,
	useMarketplaceAppInstall,
	AppInstallAnalyticsListener as AppRequestAnalyticsListener,
} from '@atlassian/emcee/app-install';
import AppRequestDialog from '@atlassian/emcee/app-request';
import { fg } from '@atlassian/jira-feature-gating';
import { type FlagConfiguration, toFlagId, useFlagsService } from '@atlassian/jira-flags';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import InstallationStatusModal from '../common/ui/main.tsx';
import { GRAPHQL_GATEWAY_PATH } from './constants';
import type {
	Flags,
	InstallationStatusConfig,
	onInstallStartedFn,
	onInstallSuccessFn,
	openMarketplaceDialogFn,
} from './types';
import { createInstallationStatusConfig, handleAppInstallFlagShow } from './utils';

type useMarketPlaceDialogType = (props: {
	requestLocation: string;
	enablePostInstallModal?: boolean;
	onDismissSuccessModal?: () => void;
}) => {
	openMarketplaceDialog: openMarketplaceDialogFn;
	marketplaceDialog: JSX.Element;
};

export const useMarketPlaceDialog: useMarketPlaceDialogType = ({
	requestLocation,
	enablePostInstallModal = true,
	onDismissSuccessModal,
}) => {
	const [isAppRequestDialogOpen, setIsAppRequestDialogOpen] = useState(false);
	const [isInstallationStatusModalShown, setIsInstallationStatusModalShown] = useState(false);

	const [installationStatusConfig, setInstallationStatusConfig] =
		useState<InstallationStatusConfig>({
			modalState: 'IN_PROGRESS',
		});

	const [selectedApp, setSelectedApp] = useState<{
		appKey: string;
		displayName: string;
	} | null>(null);

	const { showFlag, dismissFlag } = useFlagsService();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onRequestSuccess = useRef<(() => void) | undefined>();

	const cloudId = useCloudId();
	const { data: orgId } = useOrgId();
	const activationId = useActivationId();
	const environment = useEnvironment();
	const isSiteAdmin = useIsSiteAdmin();
	const isAdmin = useIsAdmin();

	const spi = makeSPI({
		baseUrl: `${window.location.origin}`,
		product: 'jira',
		subproduct: 'jira-software',
		cloudId: cloudId ?? '',
		environment,
		gqlGatewayUrl: `${window.location.origin}${GRAPHQL_GATEWAY_PATH}`,
		orgId,
		activationId,
	});
	const { installerElement, installApp } = useMarketplaceAppInstall(
		spi,
		getAppInstallConfigForEnv(environment),
		requestLocation,
	);

	const openInstallationStatusModal = (flag: FlagConfiguration): void => {
		setInstallationStatusConfig(createInstallationStatusConfig(flag));
		setIsInstallationStatusModalShown(true);
	};

	const closeInstallationStatusModal = (): void => {
		setInstallationStatusConfig({
			modalState: 'IN_PROGRESS',
		});
		setIsInstallationStatusModalShown(false);
	};

	const flags: Flags = {
		show: (flag: FlagConfiguration): void =>
			handleAppInstallFlagShow({
				flag,
				enablePostInstallModal,
				openInstallationStatusModal,
				closeInstallationStatusModal,
				showFlag,
			}),
		dismiss: (id: string) => dismissFlag(toFlagId(id)),
	};

	const installMarketplaceApp = ({
		appKey,
		onInstallSuccess,
		onInstallStarted,
		onSuccess,
		onFailure,
	}: {
		appKey: string;
		onInstallStarted?: onInstallStartedFn;
		onInstallSuccess?: onInstallSuccessFn;
		onSuccess?: () => void;
		onFailure?: () => void;
	}): void =>
		installApp(appKey, {
			flags,
			onInstallSuccess: (args) => {
				onSuccess?.();
				onInstallSuccess?.(args);
			},
			onInstallFailure: () => onFailure?.(),
			onInstallStarted: (args) => onInstallStarted?.(args),
			suppressProgressFlag: !enablePostInstallModal, // ignore progress (loading) flag if not displaying the post installation modal
		});
	const openMarketplaceDialog: openMarketplaceDialogFn = ({
		appKey,
		appName,
		isAppFree,
		onSuccess,
		onInstallSuccess,
		onInstallStarted,
		onFailure,
	}) => {
		setSelectedApp({ appKey, displayName: appName });

		fireUIAnalytics(
			createAnalyticsEvent({ action: 'clicked', actionSubject: 'button' }),
			'installProvider',
			{ isAdmin: isSiteAdmin || isAdmin, providerName: appName, appKey },
		);

		// Set the onSuccess callback for AppRequestDialog
		if (!onRequestSuccess.current) {
			onRequestSuccess.current = onSuccess;
		}

		if (isSiteAdmin || (isAdmin && isAppFree)) {
			installMarketplaceApp({
				appKey,
				onSuccess,
				onFailure,
				onInstallSuccess,
				onInstallStarted,
			});
		} else {
			setIsAppRequestDialogOpen(true);
		}
	};

	const marketplaceDialog = (
		<>
			{installerElement}
			{enablePostInstallModal && isInstallationStatusModalShown && (
				<InstallationStatusModal
					setModalShown={setIsInstallationStatusModalShown}
					modalState={installationStatusConfig.modalState}
					appName={selectedApp?.displayName ?? ''}
					configurationUrl={installationStatusConfig.configurationUrl}
					onTryAgain={installationStatusConfig.onTryAgain}
					analyticsAttributes={{
						appKey: selectedApp?.appKey,
						installLocation: requestLocation,
					}}
					{...(fg('jira_improve_scm_onboarding_gate') && { onDismissSuccessModal })}
				/>
			)}
			{isAppRequestDialogOpen && (
				<AppRequestAnalyticsListener>
					<AppRequestDialog
						config={getAppInstallConfigForEnv(environment)}
						appKey={selectedApp?.appKey}
						spi={spi}
						onClose={() => setIsAppRequestDialogOpen(false)}
						onRequestSuccess={onRequestSuccess.current}
						requestLocation={requestLocation}
						flags={flags}
					/>
				</AppRequestAnalyticsListener>
			)}
		</>
	);

	return {
		openMarketplaceDialog,
		marketplaceDialog,
	};
};
